import React, { useContext } from 'react';
import { Tooltip, Badge, NumberFormatter } from '@mantine/core';
import { ConfigContext, GlobalDataContext, MyUserDataContext } from '../contexts';
import { CustomClaims, CustomerFundData, KfhTradeAccount, Portfolio } from '../types';
import { canViewKfhTradeAccounts, convertKfhTradeAccountsToPortfolio, hasKFHTradeBalance } from '../helpers/customerDetailsHelpers';

interface PositionBadgesProps {
    portfolioData: Portfolio[];
    fundData: CustomerFundData[];
    kfhTradeAccountsData?: KfhTradeAccount[];
}

const PositionBadges: React.FC<PositionBadgesProps> = ({ portfolioData, fundData, kfhTradeAccountsData }) => {

    //use context data
    const configContext = useContext(ConfigContext);
    const globalDataContext = useContext(GlobalDataContext);
    const userContext = useContext(MyUserDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;


    // Calculate portfoliosTotalValue
    let portfoliosTotalValue = -1;
    if (portfolioData && portfolioData.length > 0) {
        portfoliosTotalValue = 0;
        portfolioData.forEach(portfolio => {
            portfoliosTotalValue += portfolio.position?.total_capital_market_value ?? 0;
        });
        portfoliosTotalValue = Math.round(portfoliosTotalValue);
    }

    // Calculate fundsTotalValue
    let fundsTotalValue = -1;
    if (fundData && fundData.length > 0) {
        fundsTotalValue = 0;
        fundData.forEach((holding) => {
            let fund = globalDataContext.funds?.find(obj => obj.code === holding.gl_code);
            const isOpenEnded = fund?.type == 'open_ended';
            const currency = holding.currency ?? 'KWD';
            let total = isOpenEnded ? (holding.eunits ?? 0) * (fund?.nav_per_unit ?? 0) : (holding.total_subscriptions ?? 0);
            if (currency === "KWD") {
                fundsTotalValue += total;
            } else {
                let rate = parseFloat(configContext.exchange_rates?.rates[currency] ?? 0);
                fundsTotalValue += (total * rate);
            }
        });
        fundsTotalValue = Math.round(fundsTotalValue);
    }

    // Calculate KFH Trade total (admin / CR only)
    let kfhTradeTotalValue = -1;
    if (canViewKfhTradeAccounts(userClaims) && kfhTradeAccountsData && hasKFHTradeBalance(kfhTradeAccountsData)) {
        const summary = convertKfhTradeAccountsToPortfolio(
            kfhTradeAccountsData,
            configContext.exchange_rates?.rates || {},
        );
        kfhTradeTotalValue = Math.round(summary.position?.total_capital_market_value ?? 0);
    }

    return (
        <>
            {fundsTotalValue >= 0 && (
                <Tooltip label="Funds">
                    <Badge color="pink" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={fundsTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
            {portfoliosTotalValue >= 0 && (
                <Tooltip label="Portfolios">
                    <Badge color="purple" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={portfoliosTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
            {kfhTradeTotalValue >= 0 && (
                <Tooltip label="KFH Trade">
                    <Badge color="orange" size="xs" rightSection="KWD" mr={5}>
                        <NumberFormatter value={kfhTradeTotalValue} thousandSeparator="," />
                    </Badge>
                </Tooltip>
            )}
        </>
    );
};

export default PositionBadges;
